/**
 * Field-app client-lib voor berichten — PIN-based fetch (X-Staff-Id / X-Staff-Pin).
 *
 * Types spiegelen `api/berichten/_helpers.ts` mapper-output en de office-app shape.
 * Bij wijzigingen: spiegel in beide locaties.
 */

import { useAuthStore } from '../stores'

// ─── Types ───────────────────────────────────────────────────────────────────

export type Richting = 'office_to_field' | 'field_to_office'
export type Status = 'open' | 'afgehandeld'
export type Prioriteit = 'normaal' | 'urgent'

export interface BerichtSummary {
  id: string
  werkOrderId: string | null
  werkOrderName: string | null
  wasserijItemId: string | null
  wasserijItemName: string | null
  customerName: string | null
  onderwerp: string | null
  categorie: string | null
  prioriteit: Prioriteit | string
  status: Status | string
  richting: Richting
  bron: string
  aangemaaktDoorEmail: string
  aangemaaktDoorNaam: string | null
  afgehandeldDoorEmail: string | null
  afgehandeldAt: string | null
  afgehandeldNote: string | null
  fotoUrls: string[]
  laatsteReactiePreview: string | null
  laatsteReactieAt: string | null
  aantalReacties: number
  createdAt: string
  updatedAt: string
}

export interface Reactie {
  id: string
  berichtId: string
  richting: Richting
  body: string
  fotoUrls: string[]
  bron: string
  auteurEmail: string
  auteurNaam: string | null
  createdAt: string
}

export interface BerichtDetail extends BerichtSummary {
  reacties: Reactie[]
}

export interface CreateBerichtInput {
  werkOrderId?: string | null
  werkOrderName?: string | null
  customerName?: string | null
  wasserijItemId?: string | null
  wasserijItemName?: string | null
  onderwerp?: string | null
  prioriteit?: Prioriteit
  body: string
  fotoUrls?: string[]
}

export interface ReplyInput {
  berichtId: string
  body: string
  fotoUrls?: string[]
}

export interface BerichtCounts {
  open: number
  total: number
}

export interface FotoUploadResult {
  path: string
  url: string
}

export const STATUS_LABELS: Record<Status, string> = {
  open: 'Open',
  afgehandeld: 'Afgehandeld',
}

// ─── Error class ─────────────────────────────────────────────────────────────

export class BerichtError extends Error {
  status: number
  constructor(message: string, status = 400) {
    super(message)
    this.name = 'BerichtError'
    this.status = status
  }
}

function authHeaders(): Record<string, string> {
  const { staff, pin } = useAuthStore.getState()
  if (!staff?.id || !pin) {
    throw new BerichtError('Niet ingelogd', 401)
  }
  return {
    'Content-Type': 'application/json',
    'X-Staff-Id': staff.id,
    'X-Staff-Pin': pin,
  }
}

async function parseError(res: Response, fallback: string): Promise<BerichtError> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null
  return new BerichtError(body?.error || fallback, res.status)
}

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(path, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) })
  if (!res.ok) throw await parseError(res, fallback)
  return res.json() as Promise<T>
}

async function getJson<T>(path: string, fallback: string): Promise<T> {
  const res = await fetch(path, { headers: authHeaders() })
  if (!res.ok) throw await parseError(res, fallback)
  return res.json() as Promise<T>
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function createBericht(input: CreateBerichtInput): Promise<{ berichtId: string }> {
  return postJson('/api/berichten/create', input, 'Bericht aanmaken mislukt')
}

export function replyBericht(input: ReplyInput): Promise<{ reactieId: string; status: Status }> {
  return postJson('/api/berichten/reply', input, 'Reactie mislukt')
}

export function resolveBericht(berichtId: string, note?: string): Promise<{ status: Status }> {
  return postJson('/api/berichten/resolve', { berichtId, note }, 'Afhandelen mislukt')
}

// ─── Reads ───────────────────────────────────────────────────────────────────

export interface BerichtListParams {
  status?: 'open' | 'afgehandeld' | 'all'
  richting?: Richting
  werkOrderId?: string
  wasserijItemId?: string
}

export async function fetchBerichtList(params: BerichtListParams = {}): Promise<BerichtSummary[]> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.richting) qs.set('richting', params.richting)
  if (params.werkOrderId) qs.set('werkOrderId', params.werkOrderId)
  if (params.wasserijItemId) qs.set('wasserijItemId', params.wasserijItemId)
  const result = await getJson<{ berichten: BerichtSummary[] }>(
    `/api/berichten/list?${qs.toString()}`,
    'Lijst ophalen mislukt'
  )
  return result.berichten
}

export function fetchBerichtCounts(): Promise<BerichtCounts> {
  return getJson<BerichtCounts>('/api/berichten/list?countOnly=1', 'Counts ophalen mislukt')
}

export function fetchBerichtDetail(id: string): Promise<BerichtDetail> {
  return getJson(`/api/berichten/detail?id=${encodeURIComponent(id)}`, 'Detail ophalen mislukt')
}

// ─── Foto-upload ─────────────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('FileReader gaf geen string terug'))
        return
      }
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('FileReader mislukt'))
    reader.readAsDataURL(file)
  })
}

export async function uploadBerichtFoto(file: File, berichtId?: string): Promise<FotoUploadResult> {
  const base64 = await fileToBase64(file)
  return postJson<FotoUploadResult>(
    '/api/berichten/upload-foto',
    {
      filename: file.name,
      contentType: file.type || 'image/jpeg',
      base64,
      berichtId: berichtId ?? null,
    },
    'Foto-upload mislukt'
  )
}

export async function refreshFotoUrl(path: string): Promise<string> {
  const { url } = await getJson<{ url: string }>(
    `/api/berichten/foto-url?path=${encodeURIComponent(path)}`,
    'Signed URL ophalen mislukt'
  )
  return url
}
